import React from "react";
import styled from "styled-components";
import { Link } from "@material-ui/core";

import { FlexBox } from "js/react/components/LayoutGrid";
import { TextContent } from "js/Components/TextContent";
import { Divider } from "js/Components/Divider";
import { Icon } from "js/Components/Icon";

import { AddressForm, type MinimalBillingAddress } from "../../../components/AddressForm/AddressForm";
import { PaymentForm } from "../../../components/PaymentForm/PaymentForm";
import { PromoCoupon, PromoForm } from "../../../components/PromoForm/PromoForm";
import { SeatsSelect } from "../../../components/SeatsSelect/SeatsSelect";
import { TermsOfUseAndRefundPolicy } from "../../../components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { formatPrice } from "../../../utils/prices";
import type { TeamCheckoutFlow } from "../TeamCheckoutFlow";

import { Button } from "js/Components/Button";
import { themeColors } from "js/react/sharedStyles";

import {
    CheckoutSwitchLink,
    ErrorMessageContainer,
    MobileFormSection,
    MobileSummarySection,
    PromoContainer,
    SeatsContainer,
    SummaryBlock,
    SummaryContent,
    SummaryRow,
    SummarySection,
    TeamPlanInternal,
} from "./TeamCheckoutStyles";
import { t } from "js/i18n";

const MAX_SEATS = 20;

export interface TeamCheckoutMobileLayoutProps {
    checkoutFlow: TeamCheckoutFlow;
    title: string;
    subTitle?: string;
    buttonLabel: string;
    error: string | null;
    seatCount: number;
    pricePerSeat: number;
    subtotal: number;
    taxTotal: number;
    dueNext: number;
    isAnnual: boolean;
    availableTrialDays: number;
    trialExpirationDate: string;
    annualSavingsPercentage: number;
    promo: { coupon: { amount_off?: number; percent_off?: number } } | null;
    initialPromotionCode?: string;
    billingAddress: MinimalBillingAddress;
    paymentMethodCreationError: string | null;
    prices: { year: { amount: number }; month?: { amount: number } };
    onPaymentElementChange: (event: unknown) => void;
    onBillingAddressChange: ({ address, isAddressValid }: { address: MinimalBillingAddress; isAddressValid: boolean }) => void;
    onSeatCountChange: (seatCount: number) => void;
    onContactSales: () => void;
    onVerifyPromoCode: (promo: unknown) => Promise<void>;
    onBillingIntervalChange: (billingInterval: "year" | "month") => void;
    onSubmit: () => void;
    canStartTrial: boolean;
    onCancel: () => void;
}

const SubmitButton = styled(Button) <{ fullWidth?: boolean }>`
    width: 100%;
`;

const CancelButton = styled(Button)`
    &&& {
        background: ${themeColors.lightGray};
        color: ${themeColors.mediumGray};
        border:none;
        width: 100%;
        justify-content: center !important;
    }
`;

export function TeamCheckoutMobileLayout({
    checkoutFlow,
    title,
    subTitle,
    buttonLabel,
    error,
    seatCount,
    pricePerSeat,
    subtotal,
    taxTotal,
    dueNext,
    isAnnual,
    availableTrialDays,
    trialExpirationDate,
    annualSavingsPercentage,
    promo,
    initialPromotionCode,
    billingAddress,
    paymentMethodCreationError,
    prices,
    onPaymentElementChange,
    onBillingAddressChange,
    onSeatCountChange,
    onContactSales,
    onVerifyPromoCode,
    onBillingIntervalChange,
    onSubmit,
    canStartTrial,
    onCancel,
}: TeamCheckoutMobileLayoutProps) {
    const showBillingIntervalSwitch = prices.month && checkoutFlow !== "reactivate";
    const showContactSupport = checkoutFlow === "reactivate";
    const isTrialFlow = checkoutFlow === "new_with_trial";
    const dueTodayAmount = isTrialFlow ? 0 : dueNext;
    const showDueTodayTax = !isTrialFlow && taxTotal > 0;
    const showTrialEndRow = isTrialFlow && availableTrialDays > 0;
    const [isSummaryExpanded, setIsSummaryExpanded] = React.useState(false);
    const handleSummaryToggle = React.useCallback(() => {
        setIsSummaryExpanded(prev => !prev);
    }, []);

    return (
        <FlexBox column horizontalAlign="start" gap={0} fillHeight>
            <MobileFormSection>
                <TextContent dialogTitle>{title}</TextContent>
                {subTitle && <TextContent normal style={{ color: "#888", fontSize: 13 }}>{subTitle}</TextContent>}
                <PaymentForm
                    variant="B"
                    paymentMethodCreationError={paymentMethodCreationError}
                    handlePaymentElementChange={onPaymentElementChange}
                />
                <AddressForm
                    variant="B"
                    initialAddress={billingAddress}
                    onChange={onBillingAddressChange}
                />
                {error && <ErrorMessageContainer>{error}</ErrorMessageContainer>}
                <TextContent disclaimer>
                    {t("By providing your card information, you allow Beautiful.ai to charge your card for future payments in accordance with their terms.")}
                </TextContent>
            </MobileFormSection>

            <MobileSummarySection>
                <FlexBox column gap={10} horizontalAlign="start" style={{ width: "100%" }}>
                    <SummaryContent $useMobileLayout>

                        <SeatsContainer $useMobileLayout>
                            <TeamPlanInternal center>Seats</TeamPlanInternal>
                            <SeatsSelect
                                disabled={checkoutFlow === "reactivate"}
                                seatCount={seatCount}
                                maxSeats={MAX_SEATS}
                                onSeatCountChange={onSeatCountChange}
                                onContactSales={onContactSales}
                            />
                        </SeatsContainer>

                        <Divider margin={15} />

                        <SummaryBlock>
                            {isSummaryExpanded && (
                                <div>
                                    <SummaryRow>
                                        <TeamPlanInternal helper>
                                            {t("Team Plan - {{billingInterval}} billing", {
                                                billingInterval: isAnnual ? t("Annual") : t("Monthly"),
                                            })}
                                        </TeamPlanInternal>
                                    </SummaryRow>
                                    <SummarySection $useMobileLayout>
                                        <SummaryRow>
                                            <TeamPlanInternal helper>
                                                {formatPrice(pricePerSeat)} x {seatCount} seats
                                            </TeamPlanInternal>
                                            <TeamPlanInternal helper>{formatPrice(subtotal)}</TeamPlanInternal>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <TeamPlanInternal helper>{t("Tax")}</TeamPlanInternal>
                                            <TeamPlanInternal helper>
                                                {taxTotal > 0 ? `+ ${formatPrice(taxTotal)}` : "—"}
                                            </TeamPlanInternal>
                                        </SummaryRow>
                                    </SummarySection>
                                    <Divider margin={15} />
                                </div>
                            )}

                            <SummarySection>
                                <SummaryRow>
                                    <TextContent sectionLabel style={{ color: "#222" }}>
                                        {t("Due today")}:&nbsp;
                                        {formatPrice(dueTodayAmount)}
                                        {showDueTodayTax ? ` ${t("plus tax")}` : ""}
                                    </TextContent>
                                    <div
                                        style={{
                                            fontFamily: "Source Sans Pro",
                                            fontSize: 10,
                                            fontStyle: "normal",
                                            fontWeight: 600,
                                            lineHeight: "normal",
                                            letterSpacing: 0.5,
                                            textTransform: "uppercase",
                                            color: "#666",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                        onClick={handleSummaryToggle}
                                    >
                                        {isSummaryExpanded ? t("Hide summary") : t("View summary")}
                                        <Icon small color="#11A9E2">
                                            {isSummaryExpanded ? "keyboard_arrow_down" : "keyboard_arrow_up"}
                                        </Icon>
                                    </div>
                                </SummaryRow>
                                {showTrialEndRow && (
                                    <SummaryRow>
                                        <TeamPlanInternal helper>
                                            {t("Due at the end of the {{period}} on {{date}}", {
                                                period: t("trial period"),
                                                date: trialExpirationDate,
                                            })}
                                        </TeamPlanInternal>
                                        <TeamPlanInternal helper>
                                            {formatPrice(dueNext)}
                                            {taxTotal > 0 ? ` ${t("plus tax")}` : ""}
                                        </TeamPlanInternal>
                                    </SummaryRow>
                                )}
                            </SummarySection>
                        </SummaryBlock>

                        <PromoContainer>
                            {!promo ? (
                                <PromoForm variant="B" verifyPromoCode={onVerifyPromoCode} initialPromotionCode={initialPromotionCode} />
                            ) : (
                                <PromoCoupon variant="B" coupon={promo.coupon} />
                            )}
                        </PromoContainer>

                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <FlexBox row verticalAlign="center" horizontalAlign="between" gap={10}>
                                {onCancel && (
                                    <CancelButton
                                        data-test-id="payment-form-cancel-button"
                                        onClick={onCancel}
                                        unframed
                                    >
                                        {t("Cancel")}
                                    </CancelButton>
                                )}
                                <SubmitButton
                                    fullWidth={!onCancel}
                                    data-test-id="payment-form-submit-button"
                                    onClick={onSubmit}
                                    disabled={!canStartTrial}
                                    blue
                                    large
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        if (!canStartTrial && (e.key === "Enter" || e.key === " ")) {
                                            e.preventDefault();
                                            onSubmit();
                                        }
                                    }}
                                >
                                    {buttonLabel}
                                </SubmitButton>
                            </FlexBox>
                            {showBillingIntervalSwitch && (
                                <TextContent disclaimer center>
                                    {isAnnual && (
                                        <>
                                            Pay {formatPrice(prices.year.amount / 12 / 100)}/mo (
                                            {formatPrice(prices.year.amount / 100)}/yr) when you{" "}
                                            <CheckoutSwitchLink onClick={() => onBillingIntervalChange("month")}>
                                                Switch to monthly billing
                                            </CheckoutSwitchLink>
                                            . &nbsp;{t("By continuing you agree to our")}&nbsp;
                                            <TermsOfUseAndRefundPolicy size="small" />
                                        </>
                                    )}
                                    {!isAnnual && (
                                        <>
                                            {t("Save {{savingsPercentage}}% when you", {
                                                savingsPercentage: annualSavingsPercentage.toFixed(0),
                                            })}{" "}
                                            <CheckoutSwitchLink onClick={() => onBillingIntervalChange("year")}>
                                                {t("switch to annual billing")}
                                            </CheckoutSwitchLink>
                                            . &nbsp;{t("By continuing you agree to our")}&nbsp;
                                            <TermsOfUseAndRefundPolicy size="small" />
                                        </>
                                    )}
                                </TextContent>
                            )}
                            {showContactSupport && (
                                <TextContent disclaimer center>
                                    <Link
                                        href="mailto:support@beautiful.ai"
                                    >{t("Contact support")}</Link> {t("for further assistance.")}
                                </TextContent>
                            )}
                        </div>
                    </SummaryContent>
                </FlexBox>
            </MobileSummarySection>
        </FlexBox>
    );
}
